import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { OpportunityDetailBody } from "../opportunities/OpportunityDetailBody.js";
import {
  markThreadSeen,
  usePortalMessages,
  usePortalOpportunity,
  usePortalSendMessage,
  usePortalSubmitQuote,
  usePortalUpdateJobStatus,
} from "../portal/api.js";

/**
 * /portal/opportunities/:invitationId (unit 17) — the unit-10 opportunity
 * view inside the signed-in shell: same component, ownership-authorized
 * data source, no magic-link token anywhere. The pre-bid question composer
 * is magic-link-only (unit 9); the Q&A list still renders.
 *
 * A closed/filled opportunity keeps its conversation history readable —
 * unlike the public page, the portal is the contractor's own record.
 */
export function PortalOpportunityDetail() {
  const { invitationId } = useParams<{ invitationId: string }>();
  const { data, isPending } = usePortalOpportunity(invitationId);
  const quote = usePortalSubmitQuote(invitationId);
  const messages = usePortalMessages(invitationId);
  const send = usePortalSendMessage(invitationId);
  const updateJobStatus = usePortalUpdateJobStatus(invitationId);

  // Opening the thread clears its unread dot in the inbox.
  const lastMessageAt = messages.data?.messages.at(-1)?.sentAt;
  useEffect(() => {
    if (invitationId && lastMessageAt) markThreadSeen(invitationId);
  }, [invitationId, lastMessageAt]);

  if (isPending || !data) {
    return (
      <div className="mx-auto max-w-md px-4 py-10">
        <div className="h-6 w-1/2 animate-pulse rounded bg-charcoal/15" />
        <div className="mt-4 h-32 animate-pulse rounded-2xl bg-charcoal/15" />
      </div>
    );
  }

  if (data.state === "ok") {
    return (
      <OpportunityDetailBody
        view={data}
        brandHeader={false}
        quoteAction={quote}
        jobAction={updateJobStatus}
        thread={{
          threadOpen: messages.data?.threadOpen ?? false,
          messages: messages.data?.messages ?? [],
          send,
        }}
      />
    );
  }

  if (data.state === "closed") {
    const history = messages.data?.messages ?? [];
    return (
      <div className="mx-auto max-w-md px-4 py-10">
        <BackLink />
        <h1 className="mt-4 text-xl font-bold text-charcoal">This request is closed</h1>
        <p className="mt-2 text-base leading-relaxed text-charcoal">
          The customer&rsquo;s request is no longer open for quotes. Your quotes and conversations
          stay right here in your history.
        </p>
        {history.length > 0 && (
          <section className="mt-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">
              Conversation history
            </h2>
            <div className="mt-2 space-y-2">
              {history.map((m) => (
                <div
                  key={m.messageId}
                  className={`max-w-[85%] rounded-2xl p-3 text-[15px] leading-relaxed ${
                    m.sender === "contractor"
                      ? "ml-auto bg-brand-700 text-white"
                      : "bg-white text-charcoal ring-1 ring-charcoal/15"
                  }`}
                >
                  <p className="whitespace-pre-wrap">{m.body}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-10">
      <BackLink />
      <h1 className="mt-4 text-xl font-bold text-charcoal">
        This invitation isn&rsquo;t in your account
      </h1>
      <p className="mt-2 text-base leading-relaxed text-charcoal">
        It may belong to a different pro profile. If you got here from an email, open the link in a
        private window — invitation links work without signing in.
      </p>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/portal" className="text-sm font-semibold text-brand-700">
      &larr; All opportunities
    </Link>
  );
}
